
import { 
  Card, 
  CardContent, 
  CardHeader 
} from '@/components/ui/card';
import { UsersStats } from '@/components/admin/users/UsersStats';
import { UsersSearch } from '@/components/admin/users/UsersSearch';
import { UsersFilter } from '@/components/admin/users/UsersFilter';
import { UsersContent } from '@/components/admin/users/UsersContent';
import { InviteUserDialog } from '@/components/admin/users/InviteUserDialog';
import { Button } from '@/components/ui/button';
import { UserPlus, RefreshCw, Users2 } from 'lucide-react';
import { useState } from 'react';
import { usePlatformAdminsData } from '@/hooks/usePlatformAdminsData';
import { useUsersFilter } from '@/hooks/useUsersFilter';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function Users() {
  const { isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  
  const {
    users,
    loading,
    refreshing,
    handleUserInvited,
    handleUserUpdated,
    handleManualRefresh
  } = usePlatformAdminsData();

  const {
    searchQuery,
    setSearchQuery,
    roleFilter,
    setRoleFilter,
    statusFilter,
    setStatusFilter,
    filteredUsers
  } = useUsersFilter(users);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading platform users...</div>
      </div>
    );
  }

  // If user is not platform admin, redirect to organization team page
  if (!isPlatformAdmin) {
    navigate('/admin/team');
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Platform Users</h1>
          <p className="text-muted-foreground">
            Manage platform administrator accounts and permissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/organization-users')}
          >
            <Users2 className="mr-2 h-4 w-4" />
            Organization Users
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setInviteDialogOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite Platform Admin
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col space-y-4">
            <UsersStats 
              totalUsers={users.length}
              onManualRefresh={handleManualRefresh}
              refreshing={refreshing}
            />
            <UsersSearch 
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
            <UsersFilter 
              roleFilter={roleFilter}
              statusFilter={statusFilter}
              onRoleFilterChange={setRoleFilter}
              onStatusFilterChange={setStatusFilter}
            />
          </div>
        </CardHeader>
        <CardContent>
          <UsersContent 
            users={users}
            filteredUsers={filteredUsers}
            onUserUpdated={handleUserUpdated}
            onManualRefresh={handleManualRefresh}
            refreshing={refreshing}
          />
        </CardContent>
      </Card>

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onUserInvited={handleUserInvited}
        forPlatformAdmins={true}
      />
    </div>
  );
}
